import { useInfiniteQuery } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { Button } from "../../components/ui/Button";
import { TextInput } from "../../components/ui/Field";
import { PageTitle, Spinner } from "../../components/ui/misc";
import { Panel } from "../../components/ui/Panel";
import { api } from "../../lib/api/endpoints";
import { formatDateTime } from "../../lib/format";
import { qk } from "../../lib/queries";

export function AuditLog() {
  const [draft, setDraft] = useState("");
  const [action, setAction] = useState<string | undefined>(undefined);
  const { data, isFetching, hasNextPage, fetchNextPage } = useInfiniteQuery({
    queryKey: qk.audit(action ?? ""),
    queryFn: ({ pageParam }) => api.audit({ before: pageParam, action, limit: 50 }),
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (p) => p.next ?? undefined,
  });
  const rows = data?.pages.flatMap((p) => p.items) ?? [];

  const filter = (e: FormEvent) => {
    e.preventDefault();
    setAction(draft.trim() || undefined);
  };

  return (
    <div>
      <PageTitle>Audit log</PageTitle>
      <form onSubmit={filter} className="mb-4 flex max-w-md gap-2">
        <TextInput
          aria-label="Action"
          placeholder="Filter by action, e.g. theme.publish"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
        />
        <Button type="submit">Filter</Button>
      </form>
      <Panel>
        <div className="overflow-x-auto">
          <table className="kd-datatable">
            <thead>
              <tr>
                <th>When</th>
                <th>Who</th>
                <th>Action</th>
                <th>Target</th>
                <th>Detail</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="align-top">
                  <td className="whitespace-nowrap text-ink-3">{formatDateTime(r.at)}</td>
                  <td>{r.user?.name ?? "System"}</td>
                  <td className="font-mono text-xs">{r.action}</td>
                  <td className="text-ink-2">{r.targetType ? `${r.targetType} ${r.targetId ?? ""}` : ""}</td>
                  <td className="font-mono text-xs break-all text-ink-3">
                    {r.detail === null || r.detail === undefined ? "" : JSON.stringify(r.detail)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
      {isFetching ? <Spinner label="Loading…" /> : null}
      {hasNextPage && !isFetching ? (
        <div className="mt-4">
          <Button onClick={() => void fetchNextPage()}>Load more</Button>
        </div>
      ) : null}
    </div>
  );
}
